import React, { useState, useEffect, useRef, ReactNode } from 'react'
import styled, { keyframes } from 'styled-components'

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`

const DropdownContainer = styled.div`
  position: relative;
  display: inline-block;
`

const DropdownButton = styled.button`
  all: unset;
  display: flex;
  align-items: center;
  padding: 8px 8px;
  font-size: 16px;
  color: ${({ theme }) => theme.text2};
  cursor: pointer;
  text-decoration: none;
  :hover {
    color: ${({ theme }) => theme.text1};
  }

  gap: 2px;
  svg {
    height: 16px;
    width: 16px;
    padding-top: 2px;
  }

  text-decoration-color: none;
  text-underline-offset: 6px;
`

const DropdownMenu = styled.div<{ isVisible: boolean }>`
  display: ${({ isVisible }) => (isVisible ? 'block' : 'none')};
  background: ${({ theme }) => theme.bg2};
  position: absolute;
  top: 100%;
  left: 0;
  min-width: 160px;
  box-shadow: 0px 8px 16px rgba(0, 0, 0, 0.2);
  z-index: 1;
  animation: ${fadeIn} 0.3s ease-out;
  margin-top: 6px;
  padding: 8px;
  border-radius: 12px;
  box-shadow: 0px 0px 1px rgba(0, 0, 0, 0.01), 0px 4px 8px rgba(0, 0, 0, 0.04), 0px 16px 24px rgba(0, 0, 0, 0.04),
    0px 24px 32px rgba(0, 0, 0, 0.01);
`

interface DropdownProps {
  buttonLabel: string
  children: ReactNode
}

const Dropdown: React.FC<DropdownProps> = ({ buttonLabel, children }) => {
  const [isVisible, setIsVisible] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  const toggleDropdown = () => setIsVisible((prev) => !prev)

  const handleClickOutside = (event: MouseEvent) => {
    if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
      setIsVisible(false)
    }
  }

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  return (
    <DropdownContainer ref={dropdownRef}>
      <DropdownButton onClick={toggleDropdown}>
        {buttonLabel}{' '}
        {isVisible ? (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="icon icon-tabler icons-tabler-outline icon-tabler-chevron-up"
          >
            <path stroke="none" d="M0 0h24v24H0z" fill="none" />
            <path d="M6 15l6 -6l6 6" />
          </svg>
        ) : (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="icon icon-tabler icons-tabler-outline icon-tabler-chevron-down"
          >
            <path stroke="none" d="M0 0h24v24H0z" fill="none" />
            <path d="M6 9l6 6l6 -6" />
          </svg>
        )}
      </DropdownButton>
      <DropdownMenu isVisible={isVisible}>{children}</DropdownMenu>
    </DropdownContainer>
  )
}

export default Dropdown
